import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronUp, ExternalLink, Minus, Plus } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { AnimalChecklistProgress } from "@/components/animals/AnimalChecklistProgress";
import { apiRequest } from "@/lib/queryClient";

// NOTE: ChecklistItem and EvaluationChecklistState are declared locally — candidates for
// extraction to a shared types file alongside AnimalData (see S8.OI.1, S8.OI.2).
interface ChecklistItem {
  definitionId: string;
  name: string;
  isComplete: boolean;
}

interface EvaluationChecklistState {
  active: ChecklistItem[];
  pending: ChecklistItem[];
  dismissed: ChecklistItem[];
  // Server returns "" (empty string) when animals.evaluation_track_id IS NULL = not yet activated.
  // Only trustworthy once the query has resolved (evalState !== undefined).
  trackId: string;
  trackName: string;
}

interface DrawerChecklistProgressProps {
  animalId: string;
  open: boolean;
}

export function DrawerChecklistProgress({ animalId, open }: DrawerChecklistProgressProps) {
  const [expanded, setExpanded] = useState(false);

  const { data: readinessItems, isLoading: readinessLoading } = useQuery<ChecklistItem[]>({
    queryKey: ["/api/animals", animalId, "profile-checklist", "readiness"],
    queryFn: () =>
      apiRequest("GET", `/api/animals/${animalId}/profile-checklist?type=readiness`),
    enabled: open && !!animalId,
  });

  const { data: evalState, isLoading: evalLoading } = useQuery<EvaluationChecklistState>({
    queryKey: ["/api/animals", animalId, "profile-checklist", "evaluation"],
    queryFn: () =>
      apiRequest("GET", `/api/animals/${animalId}/profile-checklist?type=evaluation`),
    enabled: open && !!animalId,
  });

  const isLoading = readinessLoading || evalLoading;

  const readinessDone = (readinessItems ?? []).filter((i) => i.isComplete).length;
  const readinessTotal = readinessItems?.length ?? 0;

  // Count from the active bucket only — pending and dismissed are management-UI concerns.
  const evalDone = evalState?.active.filter((i) => i.isComplete).length ?? 0;
  const evalTotal = evalState?.active.length ?? 0;

  // "" (empty string) = evaluation_track_id IS NULL. Only valid once evalState is defined.
  const evalNotActivated = evalState !== undefined && evalState.trackId === "";

  const outstandingReadiness = (readinessItems ?? []).filter((i) => !i.isComplete);
  const outstandingEval = (evalState?.active ?? []).filter((i) => !i.isComplete);
  const outstandingCount = outstandingReadiness.length + outstandingEval.length;

  // Auto-collapse when all items are completed while the drawer is open.
  useEffect(() => {
    if (outstandingCount === 0 && expanded) {
      setExpanded(false);
    }
  }, [outstandingCount, expanded]);

  if (isLoading) {
    return (
      <div className="space-y-2.5">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-2.5">
      {/* Adoption Readiness bar + Evaluation bar — hidden when expanded */}
      {!expanded && (
        <>
          <a
            href={`/animals/${animalId}?tab=profile`}
            className="block hover:opacity-80 transition-opacity"
            data-testid="link-drawer-checklist-readiness"
          >
            <AnimalChecklistProgress
              checklistType="readiness"
              done={readinessDone}
              total={readinessTotal}
            />
          </a>

          {evalNotActivated ? (
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm text-muted-foreground shrink-0">Evaluation:</span>
              <div className="flex flex-col items-end gap-0.5">
                <span className="text-xs text-muted-foreground italic">Not yet activated</span>
                <a
                  href={`/animals/${animalId}?tab=behavior&section=checklist`}
                  className="flex items-center gap-1 text-sm text-primary w-fit"
                  onClick={(e) => e.stopPropagation()}
                  data-testid="link-drawer-checklist-eval-create"
                >
                  <Plus className="h-3 w-3" />
                  Create Checklist
                </a>
              </div>
            </div>
          ) : (
            <a
              href={`/animals/${animalId}?tab=behavior&section=checklist`}
              className="block hover:opacity-80 transition-opacity"
              data-testid="link-drawer-checklist-evaluation"
            >
              <AnimalChecklistProgress
                checklistType="evaluation"
                done={evalDone}
                total={evalTotal}
              />
            </a>
          )}
        </>
      )}

      {/* Expand toggle — only rendered when there are outstanding items */}
      {outstandingCount > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          data-testid="button-drawer-checklist-toggle"
        >
          {expanded ? (
            <ChevronUp className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          )}
          {expanded
            ? "Hide"
            : `Show ${outstandingCount} outstanding item${outstandingCount !== 1 ? "s" : ""}`}
        </button>
      )}

      {/* Expanded outstanding-items panel */}
      {expanded && outstandingCount > 0 && (
        <div className="space-y-4 pt-2 border-t" data-testid="div-drawer-checklist-outstanding">
          {outstandingReadiness.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">
                Adoption Readiness{" "}
                <span className="normal-case font-normal opacity-70">
                  {readinessDone}/{readinessTotal}
                </span>
              </p>
              <ul className="space-y-1">
                {outstandingReadiness.map((item) => (
                  <li
                    key={item.definitionId}
                    className="flex items-center gap-2 text-sm"
                    data-testid={`item-outstanding-readiness-${item.definitionId}`}
                  >
                    <Minus className="h-3 w-3 text-muted-foreground shrink-0" />
                    {item.name}
                  </li>
                ))}
              </ul>
              <a
                href={`/animals/${animalId}?tab=profile`}
                className="flex items-center gap-1 text-xs text-primary w-fit mt-1.5"
                data-testid="link-drawer-checklist-manage-readiness"
              >
                Update on profile
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          )}

          {outstandingEval.length > 0 ? (
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">
                Evaluation{" "}
                <span className="normal-case font-normal opacity-70">
                  {evalDone}/{evalTotal}
                </span>
              </p>
              <ul className="space-y-1">
                {outstandingEval.map((item) => (
                  <li
                    key={item.definitionId}
                    className="flex items-center gap-2 text-sm"
                    data-testid={`item-outstanding-eval-${item.definitionId}`}
                  >
                    <Minus className="h-3 w-3 text-muted-foreground shrink-0" />
                    {item.name}
                  </li>
                ))}
              </ul>
              <a
                href={`/animals/${animalId}?tab=behavior&section=checklist`}
                className="flex items-center gap-1 text-xs text-primary w-fit mt-1.5"
                data-testid="link-drawer-checklist-manage-eval"
              >
                Update on profile
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          ) : evalNotActivated ? (
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">
                Evaluation
              </p>
              <p className="text-sm text-muted-foreground italic">Not yet activated</p>
              <a
                href={`/animals/${animalId}?tab=behavior&section=checklist`}
                className="flex items-center gap-1 text-sm text-primary w-fit"
                onClick={(e) => e.stopPropagation()}
                data-testid="link-drawer-checklist-eval-create-expanded"
              >
                <Plus className="h-3 w-3" />
                Create Checklist
              </a>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
